import { Fragment, type ReactNode } from 'react';
import { Download, Menu as MenuIcon, Star } from 'lucide-react';

import config from '@/config';
import { Menu, MenuButton } from '@shared/ui/menu';
import { T, useT } from '@shared/i18n';
import { useDialog } from '@shared/lib/hooks';
import type { UseInstallActionResult } from '@features/install-pwa';

import {
	useAppMenuTemplateItems,
	type AppMenuItemKey,
} from './app-menu.context';

/**
 * Shared, auth-independent entry point for cross-template functionality --
 * distinct from `ProfileMenu` (auth/account actions only, hidden when a
 * tenant has no cognito auth configured). There's no shared page shell in
 * this codebase yet (every page composes its own `Topbar`), so -- same as
 * `ProfileButton`/`ProfileMenu` on `basic`'s home and `pages/article`'s own
 * `MenuButton`/`Menu` -- a page wires both pieces itself: `AppMenuButton`
 * in the `Topbar`'s `right` slot, `AppMenu` rendered at the page body
 * level.
 *
 * This is a per-view menu, not one shared instance across every route --
 * each page decides its own entries by passing `items`; omitting it falls
 * back to `AppMenuConfigContext`'s template-level default (`TenantAppManifest
 * .appMenu`), so a page that wants the template's default list doesn't have
 * to repeat it, but any page can override with its own subset (or a
 * superset, if the entry is enabled -- see `enabled` below) instead of
 * being stuck with whatever every other route on the template shows.
 * Whatever list is in play, each entry still needs its own runtime
 * `enabled` check to actually render (whether *this tenant* has turned it
 * on -- e.g. `config.app.downloadedIssues.enabled`), same two-layer shape
 * `create-tenant-app.tsx`'s own `favorite`/`drafts` route filtering uses.
 * Add a new entry by adding its key to `AppMenuItemKey` and a definition
 * below.
 */
const APP_MENU_ITEMS: Record<AppMenuItemKey, AppMenuItem> = {
	// Opens `widgets/favorites-drawer`, which `shared/` can't import -- so
	// the page owning the drawer passes `onOpenFavorites`, and the entry
	// only shows on pages that do (`/downloads` shares this template's
	// default list but has no drawer).
	favorites: {
		enabled: ({ onOpenFavorites }) =>
			!!config?.app?.favorite && !!onOpenFavorites,
		render: ({ close, onOpenFavorites }) => (
			<button
				type="button"
				onClick={() => {
					close();
					onOpenFavorites?.();
				}}
			>
				<span>
					<T k="common.favorites" />
				</span>
				<span>
					<Star />
				</span>
			</button>
		),
	},
	// `onOpenDownloads` mirrors `onOpenFavorites` exactly -- there's no
	// standalone `/downloads` route to fall back to any more (removed
	// 2026-09-23, same as `/favorite/$id` before it), so like `favorites`
	// this entry only shows on a page that owns a `widgets/downloads-drawer`
	// and passes the opener in.
	downloadedIssues: {
		enabled: ({ onOpenDownloads }) =>
			!!config?.app?.downloadedIssues?.enabled && !!onOpenDownloads,
		render: ({ close, onOpenDownloads }) => (
			<button
				type="button"
				onClick={() => {
					close();
					onOpenDownloads?.();
				}}
			>
				<span>
					<T k="common.downloads" />
				</span>
				<span>
					<Download />
				</span>
			</button>
		),
	},
	// `install`'s `show`/`onClick` are computed by the call site
	// (`useInstallAction`, `@features/install-pwa`) rather than here --
	// that hook reads `AppContext`, which `shared/ui`
	// itself stays free of, matching how `onOpenFavorites` is threaded in.
	install: {
		enabled: ({ install }) => !!install?.show,
		render: ({ close, install }) => (
			<MenuButton.Install
				show={install?.show}
				onClick={() => {
					close();
					install?.onClick();
				}}
			/>
		),
	},
	// Always available, on every page's list whether it names it or not --
	// see `withSettings` below.
	settings: {
		enabled: () => true,
		render: ({ close }) => <MenuButton.Settings onOpen={close} />,
	},
	// Always available -- the topbar's own `AboutButton` (now retired in
	// favour of this entry) never gated on config either. `AboutMenuEntry`
	// below is a real component (not called as a plain function) so its
	// `useDialog()` call obeys the rules of hooks.
	about: {
		enabled: () => true,
		render: ({ close }) => <AboutMenuEntry close={close} />,
	},
	// Auth-agnostic: the page passes `onSignOut` only while someone is
	// signed in, and decides what signing out means (poets opens its
	// `LogOutDialog`). Keep it last in a list -- it's the menu's one
	// destructive row.
	signOut: {
		enabled: ({ onSignOut }) => !!onSignOut,
		render: ({ close, onSignOut }) => (
			<MenuButton.SignOut
				onClick={() => {
					close();
					onSignOut?.();
				}}
			/>
		),
	},
};

function AboutMenuEntry({ close }: { close: () => void }) {
	const { setShowDialog } = useDialog();
	return (
		<MenuButton.About
			onClick={() => {
				close();
				setShowDialog(true);
			}}
		/>
	);
}

interface AppMenuActions {
	/** Opens the page's favorites drawer; enables the `favorites` entry. */
	onOpenFavorites?: () => void;
	/** Opens the page's downloads drawer; enables the `downloadedIssues` entry. */
	onOpenDownloads?: () => void;
	/** Precomputed by `useInstallAction`; enables the `install` entry. */
	install?: UseInstallActionResult;
	/** Signs the reader out; enables the `signOut` entry. Omit while signed out. */
	onSignOut?: () => void;
}

/**
 * A row a template adds to the top of its menu, outside the shared
 * `AppMenuItemKey` registry -- for navigation only that template has (e.g.
 * poets' feed sections). `active` marks the one the reader is on.
 */
export interface AppMenuTemplateItem {
	id: string;
	label: string;
	icon: ReactNode;
	active?: boolean;
	onSelect: () => void;
}

interface AppMenuItem {
	enabled: (actions: AppMenuActions) => boolean;
	render: (args: AppMenuActions & { close: () => void }) => ReactNode;
}

/**
 * Every general menu has Settings, so no list has to remember it: it goes
 * just above `about`, else just above `signOut` (which stays last), else
 * last. This also means the menu button always shows now, even on a page
 * whose other entries are all disabled.
 */
export function withSettings(
	items: ReadonlyArray<AppMenuItemKey>,
): Array<AppMenuItemKey> {
	if (items.includes('settings')) {
		return [...items];
	}
	const about = items.indexOf('about');
	const at = about === -1 ? items.indexOf('signOut') : about;
	return at === -1
		? [...items, 'settings']
		: [...items.slice(0, at), 'settings', ...items.slice(at)];
}

function useVisibleAppMenuItems(
	items: Array<AppMenuItemKey> | undefined,
	actions: AppMenuActions,
): Array<AppMenuItemKey> {
	const templateDefault = useAppMenuTemplateItems();
	return withSettings(items ?? templateDefault).filter((key) =>
		APP_MENU_ITEMS[key]?.enabled(actions),
	);
}

export function AppMenuButton({
	onClick,
	items,
	...actions
}: AppMenuButtonProps) {
	const t = useT();
	if (!useVisibleAppMenuItems(items, actions).length) {
		return null;
	}

	return (
		<button
			type="button"
			className="tooltip down"
			data-text={t('common.menu')}
			aria-label={t('common.menu')}
			data-menu-toggle
			onClick={onClick}
		>
			<MenuIcon color="var(--topbar-buttons-stroke)" size={24} />
		</button>
	);
}

interface AppMenuButtonProps extends AppMenuActions {
	onClick: () => void;
	/** Overrides the template's default entries for this view. */
	items?: Array<AppMenuItemKey>;
}

export function AppMenu({
	open,
	setOpen,
	items,
	templateItems,
	...actions
}: AppMenuProps) {
	const visibleItems = useVisibleAppMenuItems(items, actions);
	if (!visibleItems.length) {
		return null;
	}

	const close = () => setOpen(false);
	return (
		<Menu isOpen={open} setIsMenuOpen={setOpen}>
			<>
				{templateItems?.map((item) => (
					<MenuButton.Item
						key={item.id}
						label={item.label}
						icon={item.icon}
						active={item.active}
						onClick={() => {
							close();
							item.onSelect();
						}}
					/>
				))}
				{visibleItems.map((key) => (
					<Fragment key={key}>
						{APP_MENU_ITEMS[key].render({ ...actions, close })}
					</Fragment>
				))}
			</>
		</Menu>
	);
}

interface AppMenuProps extends AppMenuActions {
	open: boolean;
	setOpen: (v: boolean) => void;
	/** Overrides the template's default entries for this view. */
	items?: Array<AppMenuItemKey>;
	/** Template-specific rows, rendered above the shared entries. */
	templateItems?: Array<AppMenuTemplateItem>;
}
